/**
 * Built-in checks that run on every document unless a config disables them
 * (spec §12.3). They walk the payload, so they find a field wherever it sits.
 * Table checks (`total-mismatch`) are raised by buildView, which owns tables.
 */
import { isPlainObject } from "../normalizeExtract";
import { humanize } from "../format/labels";
import { addressMissing, isAddressLike } from "../format/registry";
import { agoText, displayDate, isBefore, monthsBetween, nowFields, parseDateLike } from "../format/dates";

export const EXPIRY_KEY = /expir(y|ation)|expires/i;

const walk = (value, path, key, parentKey, fn) => {
  fn(key, value, path, parentKey);
  if (isPlainObject(value)) {
    if (isAddressLike(value)) return;
    Object.entries(value).forEach(([k, v]) =>
      walk(v, path ? `${path}.${k}` : k, k, key, fn),
    );
  } else if (Array.isArray(value)) {
    value.forEach((v, i) => walk(v, `${path}[${i}]`, key, parentKey, fn));
  }
};

/** Expired-date note for a row (shared with buildNode so row + check agree). */
export const expiredNote = (key, value, now) => {
  if (!EXPIRY_KEY.test(String(key)) || typeof value !== "string") return null;
  const d = parseDateLike(value);
  const today = nowFields(now);
  if (!d || !isBefore(d, today)) return null;
  return { date: d, months: monthsBetween(d, today) };
};

/** "CRITICAL: Name mismatch — the ID reads …" → { title, detail } */
export const splitCritical = (text) => {
  const body = String(text).replace(/^CRITICAL[\s:—–-]*/i, "").trim();
  const m = body.match(/^(.+?)(?:\s[—–-]\s|[.;:](?:\s|$))(.*)$/s);
  if (!m) return { title: body, detail: undefined };
  return { title: m[1].trim(), detail: m[2].trim() || undefined };
};

export const runBuiltins = (payload, { now, disabled = [] }) => {
  const off = new Set(disabled);
  const checks = [];
  const seen = new Set();
  const push = (c) => {
    const id = c.id;
    if (seen.has(id)) return;
    seen.add(id);
    checks.push(c);
  };

  walk(payload, "", null, null, (key, value, path, parentKey) => {
    if (key === null) return;
    if (!off.has("expired")) {
      const exp = expiredNote(key, value, now);
      if (exp) {
        push({
          id: `expired:${path}`,
          tone: "crit",
          title: `${humanize(key, { parentKey })} passed on ${displayDate(exp.date)}`,
          detail: `Expired ${agoText(exp.months)}.`,
          target: { path },
        });
      }
    }
    if (!off.has("critical-note") && typeof value === "string" && /^CRITICAL/i.test(value.trim())) {
      const { title, detail } = splitCritical(value.trim());
      push({ id: `critical-note:${path}`, tone: "crit", title, detail, target: { path } });
    }
    if (!off.has("address-incomplete") && isAddressLike(value)) {
      const missing = addressMissing(value);
      if (missing) {
        push({
          id: `address-incomplete:${path}`,
          tone: "warn",
          title: `${humanize(key, { parentKey })} is missing ${missing}`,
          target: { path },
        });
      }
    }
    if (!off.has("re-upload") && key === "requires_re_upload" && value === true) {
      const parentPath = path.replace(/\.?requires_re_upload$/, "");
      const reason = parentPath
        ? payload && getReason(payload, parentPath)
        : payload?.re_upload_reason;
      push({
        id: `re-upload:${path}`,
        tone: "crit",
        title: "Re-upload required",
        detail: reason || undefined,
        target: { path },
      });
    }
  });

  if (!off.has("low-confidence") && isPlainObject(payload)) {
    const raw =
      payload.extraction_confidence ?? payload.extractionConfidence ?? payload.confidence;
    const n = typeof raw === "number" ? (raw > 1 ? raw / 100 : raw) : null;
    if (n !== null && n < 0.6) {
      const illegible = payload.legible === false || String(payload.legible).toLowerCase() === "no";
      push({
        id: "low-confidence",
        tone: "warn",
        title: `Low extraction confidence (${Math.round(n * 100)}%)${illegible ? " and image not legible" : ""}`,
        target: {
          path: ["extraction_confidence", "extractionConfidence", "confidence"].find(
            (k) => payload[k] !== undefined,
          ),
        },
      });
    }
  }
  return checks;
};

const getReason = (payload, parentPath) => {
  const parts = parentPath.match(/[^.[\]]+/g) || [];
  let cur = payload;
  for (const p of parts) cur = cur?.[/^\d+$/.test(p) ? Number(p) : p];
  return cur?.re_upload_reason;
};
